import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Header from '../components/Header';
import ProjectCard from '../components/ProjectCard';
import StatusLegend from '../components/StatusLegend';
import TaskCard from '../components/TaskCard';
import DiscussionModal from '../components/DiscussionModal';
import { useProjectStore } from '../store/useProjectStore';
import { mockProject } from '../utils/mockData';
import { Task } from '../types';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#f5f7fa',
  paddingBottom: theme.spacing(6),
}));

const ContentWrapper = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  [theme.breakpoints.up('lg')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}));

const TasksSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const SectionHeader = styled(Paper)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  borderRadius: theme.spacing(1.5),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#1a202c',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const TaskCount = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#64748b',
  backgroundColor: '#f1f5f9',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.spacing(2),
  marginLeft: theme.spacing(1.5),
}));

const HeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

const TasksGrid = styled(Box)<{ layoutMode: 'grid' | 'masonry' }>(({ theme, layoutMode }) => ({
  ...(layoutMode === 'grid' ? {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: theme.spacing(3),
    alignItems: 'start',
    [theme.breakpoints.up('xl')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))',
      gap: theme.spacing(3.5),
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: theme.spacing(2.5),
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
    },
  } : {
    columnCount: 3,
    columnGap: theme.spacing(3),
    [theme.breakpoints.up('xl')]: {
      columnCount: 3,
      columnGap: theme.spacing(3.5),
    },
    [theme.breakpoints.down('lg')]: {
      columnCount: 2,
      columnGap: theme.spacing(2.5),
    },
    [theme.breakpoints.down('md')]: {
      columnCount: 2,
      columnGap: theme.spacing(2.5),
    },
    [theme.breakpoints.down('sm')]: {
      columnCount: 1,
      columnGap: 0,
    },
    '& > *': {
      breakInside: 'avoid',
      marginBottom: theme.spacing(3),
      display: 'block',
      width: '100%',
    },
  }),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1, 2),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));

const LayoutToggleGroup = styled(ButtonGroup)(({ theme }) => ({
  backgroundColor: '#ffffff',
  '& .MuiIconButton-root': {
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    border: 'none',
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
    '&.active': {
      backgroundColor: theme.palette.primary.main,
      color: '#ffffff',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

const HomePage: React.FC = () => {
  const { project, setProject, toggleAllTasks, layoutMode, setLayoutMode } = useProjectStore();
  const [allExpanded, setAllExpanded] = useState(true);
  const [discussionModal, setDiscussionModal] = useState<{
    open: boolean;
    taskId: string;
    taskTitle: string;
    subtaskId?: string;
    subtaskTitle?: string;
  }>({ open: false, taskId: '', taskTitle: '' });

  useEffect(() => {
    // Initialize with mock data if no project
    if (!project) {
      setProject(mockProject);
    }
  }, [project, setProject]);

  const handleToggleAll = () => {
    const newExpanded = !allExpanded;
    setAllExpanded(newExpanded);
    toggleAllTasks(!newExpanded);
  };

  const handleOpenDiscussion = (taskId: string, subtaskId: string | null) => {
    if (!project) return;
    
    const task = project.tasks.find(t => t.id === taskId);
    if (!task) return;
    
    let subtaskTitle: string | undefined;
    if (subtaskId) {
      const subtask = task.subtasks.find(st => st.id === subtaskId);
      subtaskTitle = subtask?.text;
    }
    
    setDiscussionModal({
      open: true,
      taskId,
      taskTitle: task.title,
      subtaskId: subtaskId || undefined,
      subtaskTitle
    });
  };

  const handleCloseDiscussion = () => {
    setDiscussionModal({ open: false, taskId: '', taskTitle: '' });
  };

  if (!project) {
    return (
      <PageContainer>
        <Header />
        <ContentWrapper maxWidth="xl">
          <Typography>載入中...</Typography>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header />
      <ContentWrapper maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <ProjectCard project={project} />
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <StatusLegend />
        </Box>
        
        <TasksSection>
          <SectionHeader elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SectionTitle>
                任務總覽
              </SectionTitle>
              <TaskCount>
                {project.tasks.length} 個任務
              </TaskCount>
            </Box>
            <HeaderActions>
              <StyledButton
                variant="outlined"
                size="medium"
                startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                onClick={handleToggleAll}
              >
                {allExpanded ? '全部折疊' : '全部展開'}
              </StyledButton>
              
              <LayoutToggleGroup size="small">
                <Tooltip title="網格布局">
                  <IconButton
                    onClick={() => setLayoutMode('grid')}
                    className={layoutMode === 'grid' ? 'active' : ''}
                  >
                    <ViewModuleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="瀑布流布局">
                  <IconButton
                    onClick={() => setLayoutMode('masonry')}
                    className={layoutMode === 'masonry' ? 'active' : ''}
                  >
                    <ViewColumnIcon />
                  </IconButton>
                </Tooltip>
              </LayoutToggleGroup>
            </HeaderActions>
          </SectionHeader>

          <TasksGrid layoutMode={layoutMode}>
            {project.tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onOpenDiscussion={handleOpenDiscussion}
              />
            ))}
          </TasksGrid>
        </TasksSection>
      </ContentWrapper>

      {/* Discussion Modal */}
      <DiscussionModal
        open={discussionModal.open}
        onClose={handleCloseDiscussion}
        taskId={discussionModal.taskId}
        taskTitle={discussionModal.taskTitle}
        subtaskId={discussionModal.subtaskId}
        subtaskTitle={discussionModal.subtaskTitle}
      />
    </PageContainer>
  );
};

export default HomePage; 